import React, { useContext } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Sun, Moon } from 'lucide-react';
import { ThemeContext } from '../../context/ThemeContext';
import BRAND from '../../constants/brand';
import { NAV_LINKS } from '../../constants/siteMap';


// Used on the landing page itself (plain #anchor, scrolls in place) and on
// standalone public pages like /contact or /terms (prefixed with /, so the
// same click takes you back to the landing page and then to that section).
export default function PublicNav({ anchorPrefix = '' }) {
  // The public pages already FOLLOWED the operating system's dark preference —
  // ThemeProvider wraps the whole app, signed in or not — but the only control
  // for it lived in the signed-in Header. So a visitor on a light laptop who
  // prefers dark had no way to say so until after they had registered, which is
  // the wrong order.
  const { isDark, toggle } = useContext(ThemeContext) || {};
  // Which page the visitor is on, so the bar can say so.
  const { pathname, hash } = useLocation();

  return (
    // Tokens rather than `bg-white/80`. The dark-mode bridge in index.css
    // rewrites literal `bg-white`, but not the `/80` opacity variant — which
    // left this bar pale grey with unreadable text on the dark theme.
    <header className="sticky top-0 z-30 bg-surface/80 backdrop-blur border-b border-line">
      <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
        <Link to="/" className="shrink-0">
          {/* A span, not an h1. The wordmark is the site's identity, not the page's heading — an h1 here gave every page two of them, which splits the one signal search engines use to decide what a page is about. */}
          <span className="block text-xl font-bold brand-text leading-tight">{BRAND.name}</span>
          {/* Dropped on phones. It wrapped to two lines at 360px and pushed the
              bar to nearly twice its height for a strapline nobody reads there.
              `sm:` and not a custom `xs:` — there is no xs breakpoint in this
              Tailwind config, so `xs:block` would have matched nothing and
              hidden the strapline at every width instead of just small ones. */}
          <p className="hidden sm:block text-[11px] text-ink-3 -mt-0.5 whitespace-nowrap">
          </p>
        </Link>
        <nav className="hidden md:flex items-center gap-6">
          {NAV_LINKS.map((l) => {
            const active = l.to
              ? pathname === l.to
              // An anchor is only 'where you are' while you are on the page it
              // scrolls within, and only once it has actually been used.
              : pathname === '/' && hash === l.hash;
            const cls = `relative text-sm font-medium transition ${
              active ? 'text-brand-500' : 'text-ink-2 hover:text-brand-500'}`;
            // The underline is the part that reads at a glance; colour alone
            // is a weak signal and no signal at all to anyone who cannot
            // separate these two hues.
            const bar = active && (
              <span className="absolute -bottom-1.5 left-0 right-0 h-0.5 rounded-full bg-brand-500" />
            );
            return l.to ? (
              <Link key={l.to} to={l.to} className={cls} aria-current={active ? 'page' : undefined}>
                {l.label}{bar}
              </Link>
            ) : (
              <a key={l.hash} href={`${anchorPrefix}${l.hash}`} className={cls}>
                {l.label}{bar}
              </a>
            );
          })}
        </nav>
        <div className="flex items-center gap-2 sm:gap-3">
          {/* Same control as the signed-in header, deliberately: it flips to
              the opposite of what is on screen rather than cycling through a
              third "system" state nobody can predict from one button. */}
          {toggle && (
            <button
              type="button"
              onClick={toggle}
              title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              // shrink-0 because the nav row is a flex container: at 360px the
              // Sign Up button won the space and squashed this to 31x36, a
              // visibly non-square square.
              className="shrink-0 w-9 h-9 rounded-lg border border-line text-ink-2 hover:bg-surface-3
                         flex items-center justify-center transition-colors"
            >
              {isDark ? <Sun size={16} /> : <Moon size={16} />}
            </button>
          )}
          <Link to="/login" className="text-sm font-semibold text-ink hover:text-brand-500 px-3 py-2.5">Login</Link>
          <Link to="/register" className="shrink-0 whitespace-nowrap text-sm font-semibold bg-brand-gradient text-white px-3 sm:px-4 py-2 rounded-lg hover:shadow-glow transition">
            Sign Up Free
          </Link>
        </div>
      </div>
    </header>
  );
}
